import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import { Await, defer, Link, NavLink, useLoaderData, useSubmit } from 'react-router-dom';
import { Search } from '../../components/Search/Search';
import { checkAuth } from '../../utils/auth';
import { client, getErrorMessage } from '../../utils/client';

type KeywordPageProps = {
  keywords: { keyword: string; mentions: number }[];
  next?: string;
};

type Response = {
  response: KeywordPageProps;
};

const ProbableKeywordPage: FC = () => {
  const [period, setPeriod] = useState('day');
  const [responseData, setResponseData] = useState<KeywordPageProps>();

  const { response } = useLoaderData() as Response;

  const submit = useSubmit();

  useEffect(() => {
    setResponseData(response);
      resetTweets();
  }, [response]);

  const handlePeriodChange = useCallback(
      (period: string) => () => {
        setPeriod(period);
      },
      []
  );

  const handleAction = useCallback(
      (act: string, id: string) => async () => {
        const formData = new FormData();
        formData.append('keyword', id);
        if (act === 'ignore') {
          formData.append('ignore', 'true');
        }
        if (act === 'blacklist') {
          formData.append('ignore', 'true');
          formData.append('blacklist', 'true');
        }
        await submit(formData, { method: act === 'delete' ? 'delete' : 'put' });
      },
      [submit]
  );

  const resetTweets = async () => {
    const updatedResponse = await client.get(`/admin/keyword/probable?period=${period}`).then((res) => res.data);
    setResponseData(updatedResponse);
  };

  useEffect(() => {
    resetTweets();
  }, [period]);

  return (
    <>
      <div className="flex flex-row justify-between items-center gap-4">
        <h1>Probable Keywords</h1>
        <div className="flex flex-row justify-end items-center gap-4">
          <Search />
          <NavLink to="/keywords/ef">Show filtered</NavLink>
          <button onClick={handlePeriodChange("hour")}>Hour</button>
          <button onClick={handlePeriodChange("day")}>Day</button>
          <button onClick={handlePeriodChange("week")}>Week</button>
        </div>
      </div>
      <Suspense
        fallback={<p>Loading keywords...</p>}
      >
        <Await
          resolve={responseData || response}
          errorElement={
            <p>Error loading keywords...</p>
          }
        >
          {(res: KeywordPageProps) => (
            <table className="w-full">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Mentions</th>
              </tr>
            </thead>
            <tbody>
            {res.keywords.map(item => (
              <tr key={item.keyword}>
                <td>
                  <Link to={`/admin/feed?q=${item.keyword}`}>{item.keyword}</Link>
                </td>
                <td>
                  <div className="flex flex-row justify-between items-center">
                    <span>{item.mentions}</span>
                    <div className="flex flex-row justify-end items-center gap-2">
                      <button onClick={handleAction('confirm', item.keyword)}>Confirm</button>
                      <button onClick={handleAction('ignore', item.keyword)}>Ignore</button>
                      <button onClick={handleAction('blacklist', item.keyword)}>Blacklist</button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {res.next && (
              <tr>
                <td>
                  <NavLink to={`/admin/keywords/probable?cursor=${res.next}`}>Load more</NavLink>
                </td>
              </tr>
            )}
            </tbody>
          </table>
          )}
        </Await>
      </Suspense>
    </>
  );
};

export const loadProbableKeywords = async ({ request }: any) => {
  if (!await checkAuth()) return null;
  const url = new URL(request.url);
  const response = client.get(`/admin/keyword/probable${url.search}`).then(response => response.data);
  return defer({ response });
};

export const addProbableKeyword = async ({ request }: { request: Request }) => {
  try {
    const formData = await request.formData();
    if (request.method.toLowerCase() === 'put') {
      return await client.put(`/admin/keyword/probable`, {
        keyword: formData.get('keyword'),
        ignore: formData.get('ignore') === 'true',
        blacklist: formData.get('blacklist') === 'true',
      });
    }
  } catch (err) {
    return { error: getErrorMessage(err) };
  }
};


export default ProbableKeywordPage;
